import { ReactNode, useMemo } from 'react';
import { Provider } from 'react-redux';
import { createStore, type RootState } from '../store/store';
import { menuApiRedux, type MenuApiResponse } from '@/shared/features/menu/infra/adapters/menuApiSlice';
import { homePageApiRedux } from '@/features/home/infra/adapters/home.adapter';
import type { RtkQueryAdapter } from '@/shared/features/query/infra/adapters/rtk.adapter';

type StoreType = ReturnType<typeof createStore>;

interface ReduxProviderProps {
  /** SSR only: when set, use this store (entry-server may have home from executeQuery; we seed menu via initializeCache). */
  store?: StoreType;
  children: ReactNode;
  preloadedState?: Partial<RootState>;
  /** Full menu API response (hamburger + desktop) from SSR/window – used to seed RTK Query cache. */
  initialMenuResult?: MenuApiResponse | null;
  initialHomePageData?: Record<string, unknown> | null;
}

function initializeCache(
  store: StoreType,
  apiRedux: unknown,
  endpointName: string,
  args: unknown,
  data: unknown
): void {
  if (!apiRedux || typeof apiRedux !== 'object' || apiRedux === null || !('getApi' in apiRedux)) return;
  try {
    const rtkApi = (apiRedux as RtkQueryAdapter).getApi();
    if (rtkApi?.util?.upsertQueryData) {
      store.dispatch(rtkApi.util.upsertQueryData(endpointName, args, data));
    }
  } catch (error) {
    console.warn(`Failed to initialize ${endpointName} cache:`, error);
  }
}

/**
 * Redux Provider Component
 * - SSR: uses store from entry-server (has home from executeQuery). We seed menu (and home, userContext) into that store
 *   via initializeCache so first render has menu + home sections in HTML.
 * - Client: creates a new store and seeds menu, home, userContext from window.__INITIAL_*__
 *   so useGetMenuQuery / useGetHomePageDataQuery return data on first render (SSR HTML and hydration).
 */
export function ReduxProvider({
  store: storeProp,
  children,
  preloadedState,
  initialMenuResult,
  initialHomePageData,
}: ReduxProviderProps) {
  const store = useMemo(() => {
    const createdStore = storeProp ?? createStore(preloadedState as Record<string, unknown> | undefined);
    if (!createdStore) return createdStore;

    if (initialMenuResult) {
      initializeCache(createdStore, menuApiRedux, 'getMenu', undefined, initialMenuResult);
    }
    if (initialHomePageData) {
      let toCache: unknown = initialHomePageData;
      if (typeof toCache === 'string') {
        try {
          const parsed = JSON.parse(toCache);
          toCache = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        } catch {
          toCache = initialHomePageData;
        }
      }
      initializeCache(createdStore, homePageApiRedux, 'getHomePageData', {}, toCache);
    }

    return createdStore;
  }, [storeProp, preloadedState, initialMenuResult, initialHomePageData]);

  return <Provider store={store}>{children}</Provider>;
}
